import axios from 'axios';
import { useAuthStore } from '../store';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:44641';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

// Attach Bearer token from localStorage on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cast_token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

// The API issues a single stateless JWT (no refresh-token flow) — once it expires or is
// otherwise rejected, every authenticated call will 401 until the user signs in again. Handle
// that centrally here rather than in every page: clear the stale session and send them back to
// Login, carrying the page they were on so they land back where they left off.
//
// Exception: /api/Payout/Heartbeat. A 401 there just means "this session's watch time can't be
// credited right now" — forcing a redirect would yank someone out of a movie they're mid-way
// through over a background call they never noticed. CinemaPlayer handles that case itself by
// queuing the heartbeat (see utils/pendingHeartbeat.js) and flushing it next time they sign in.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    const isHeartbeat = url.includes('/api/Payout/Heartbeat');

    if (status === 401 && !isHeartbeat) {
      const wasAuthed = !!localStorage.getItem('cast_token');
      useAuthStore.getState().logout();

      if (wasAuthed && typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        const redirect = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/login?sessionExpired=1&redirect=${redirect}`;
      }
    }

    return Promise.reject(error);
  }
);

// ---- Auth ------------------------------------------------------------------
export const authApi = {
  register: (fullName, username, email, password, role = 'User') =>
    api.post('/api/Auth/Register', { fullName, username, email, password, role }),

  login: (identifier, password) =>
    api.post('/api/Auth/Login', { identifier, password }),

  activateAccount: (userId, token) =>
    api.post('/api/Auth/ActivateAccount', { userId, token }),

  resendActivation: (email) =>
    api.post('/api/Auth/ResendActivation', { email }),

  forgotPassword: (email) =>
    api.post('/api/Auth/ForgotPassword', { email }),

  resetPassword: (userId, token, newPassword) =>
    api.post('/api/Auth/ResetPassword', { userId, token, newPassword }),

  getProfile: () =>
    api.get('/api/Auth/Profile'),

  updateProfile: (data) =>
    api.put('/api/Auth/Profile', data),

  logout: () =>
    api.post('/api/Auth/Logout'),

  deleteAccount: (password) =>
    api.post('/api/Auth/DeleteAccount', { password }),
};

// ---- Viewer profiles (Netflix-style "who's watching") ----------------------
// Every account holds 1–5 of these; the person picks one after login (see WhosWatchingPage)
// and that choice is baked into the JWT (profileId/isKids claims) via `select`.
export const profileApi = {
  list: () => api.get('/api/Profile'),
  create: ({ name, avatarUrl, type, pin }) =>
    api.post('/api/Profile', { name, avatarUrl, type, pin }),
  update: (id, { name, avatarUrl, type, pin }) =>
    api.put(`/api/Profile/${id}`, { name, avatarUrl, type, pin }),
  remove: (id) => api.delete(`/api/Profile/${id}`),
  // pin is only required if the target profile has one set (server replies 423 if so and none given)
  select: (id, pin) => api.post(`/api/Profile/${id}/Select`, pin ? { pin } : {}),
  // Upload/replace this profile's avatar image.
  uploadAvatar: (id, file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post(`/api/Profile/${id}/Avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ---- Movies (MovieApi) ------------------------------------------------------
export const movieApi = {
  getAll: () =>
    api.get('/api/MovieApi/GetAllMovies'),

  getDetail: (movieId) =>
    api.get('/api/MovieApi/GetMovieDetail', { params: { movieId } }),

  getByCategory: (categoryId, search) =>
    api.get('/api/MovieApi/GetMoviesByCategory', { params: { categoryId, search: search || undefined } }),

  // General catalogue search (Title/Director/Cast/Description). categoryId is optional.
  // Same endpoint used by the mobile app.
  search: (query, categoryId) =>
    api.get('/api/MovieApi/Search', { params: { query: query || undefined, categoryId: categoryId || undefined } }),

  getCategories: () =>
    api.get('/api/MovieApi/GetCategories'),

  getEpisodeDetail: (episodeId) =>
    api.get('/api/MovieApi/GetEpisodeDetail', { params: { episodeId } }),

  reactToEpisode: (episodeId, isLike) =>
    api.post('/api/MovieApi/ReactToEpisode', null, { params: { episodeId, isLike } }),

  // New reaction API: reaction is Like, Dislike, DoubleLike, or None (to remove it).
  setEpisodeReaction: (episodeId, reaction) =>
    api.put(`/api/MovieApi/Episodes/${episodeId}/Reaction`, { reaction }),
  setMovieReaction: (movieId, reaction) =>
    api.put(`/api/MovieApi/Movies/${movieId}/Reaction`, { reaction }),

  postComment: (episodeId, commentText) =>
    api.post('/api/MovieApi/PostComment', null, { params: { episodeId, commentText } }),

  deleteComment: (commentId) =>
    api.post('/api/MovieApi/DeleteComment', null, { params: { commentId } }),

  getComments: (episodeId, page = 1, pageSize = 20) =>
    api.get('/api/MovieApi/GetComments', { params: { episodeId, page, pageSize } }),

  checkSubscription: () =>
    api.get('/api/MovieApi/CheckSubscriptionStatus'),

  uploadMovie: (formData, onUploadProgress) =>
    api.post('/api/MovieApi/UploadMovie', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      timeout: 0, // large video files can take a while; don't let the global 30s timeout cut this off
    }),

  uploadEpisode: (formData, onUploadProgress) =>
    api.post('/api/MovieApi/UploadEpisode', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      timeout: 0,
    }),

  // Content creator: get their own uploads
  getMyMovies: () =>
    api.get('/api/MovieApi/GetMyMovies'),

  // Content creator: propose changes to a title they own. Nothing on the live title changes until
  // an admin approves it. Pass only the fields that actually changed; omitted fields mean "no change"
  // (an empty string clears a text field).
  submitMovieEdit: (movieId, { title, description, director, cast, releaseYear, categoryId, categoryIds, thumbnailFile, posterFile, trailerFile, isKidsContent, rating, classificationAuthority, contentDescriptors }, onUploadProgress) => {
    const formData = new FormData();
    if (title != null) formData.append('Title', title);
    if (description != null) formData.append('Description', description);
    if (director != null) formData.append('Director', director);
    if (cast != null) formData.append('Cast', cast);
    if (releaseYear != null) formData.append('ReleaseYear', releaseYear);
    if (categoryId != null) formData.append('CategoryId', categoryId);
    if (categoryIds != null) categoryIds.forEach((id) => formData.append('CategoryIds', id));
    if (thumbnailFile) formData.append('Thumbnail', thumbnailFile);
    if (trailerFile) formData.append('Trailer', trailerFile);
    if (isKidsContent != null) formData.append('IsKidsContent', isKidsContent);
    if (rating != null) formData.append('Rating', rating);
    if (classificationAuthority != null) formData.append('ClassificationAuthority', classificationAuthority);
    if (contentDescriptors != null) formData.append('ContentDescriptors', contentDescriptors);
    if (posterFile) formData.append('Poster', posterFile);
    return api.post('/api/MovieApi/SubmitMovieEdit', formData, {
      params: { movieId },
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      timeout: 0, // a replacement trailer can be a large file
    });
  },

  // Content creator: withdraw their own edit while it's still pending review.
  cancelMovieEdit: (editId) =>
    api.delete('/api/MovieApi/CancelMovieEdit', { params: { editId } }),

  // Content creator: same idea for an episode of a title they own (title, description, episode/season
  // number, replacement video). For a plain Movie, its main video is Episode 1.
  submitEpisodeEdit: (episodeId, { title, description, episodeNumber, seasonNumber, videoFile }, onUploadProgress) => {
    const formData = new FormData();
    if (title != null) formData.append('Title', title);
    if (description != null) formData.append('Description', description);
    if (episodeNumber != null) formData.append('EpisodeNumber', episodeNumber);
    if (seasonNumber != null) formData.append('SeasonNumber', seasonNumber);
    if (videoFile) formData.append('VideoFile', videoFile);
    return api.post('/api/MovieApi/SubmitEpisodeEdit', formData, {
      params: { episodeId },
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      timeout: 0, // a replacement video can be a very large file
    });
  },
  cancelEpisodeEdit: (editId) =>
    api.delete('/api/MovieApi/CancelEpisodeEdit', { params: { editId } }),

  // Cast / Actors
  getMovieCast: (movieId) => api.get('/api/MovieApi/GetMovieCast', { params: { movieId } }),
  getMoviesByActor: (actorId) => api.get('/api/MovieApi/GetMoviesByActor', { params: { actorId } }),
  getActors: () => api.get('/api/MovieApi/GetActors'),

  // My List
  getMyList: () => api.get('/api/MovieApi/GetMyList'),
  addToMyList: (movieId) => api.post('/api/MovieApi/AddToMyList', null, { params: { movieId } }),
  removeFromMyList: (movieId) => api.delete('/api/MovieApi/RemoveFromMyList', { params: { movieId } }),
};

// ---- Home screen -------------------------------------------------------------
export const homeApi = {
  getCarousel: (limit = 5) => api.get('/api/Home/Carousel', { params: { limit } }),
  getTop10: () => api.get('/api/Home/Top10'),
  getQuickLists: () => api.get('/api/Home/QuickLists'),
  getNewReleases: (limit = 10) => api.get('/api/Home/NewReleases', { params: { limit } }),
  getContinueWatching: () => api.get('/api/Home/ContinueWatching'),
  updateWatchProgress: (episodeId, positionSeconds, durationSeconds) =>
    api.post('/api/Home/UpdateWatchProgress', { episodeId, positionSeconds, durationSeconds }),
  getTvSeries: (page = 1, pageSize = 20) => api.get('/api/Home/TvSeries', { params: { page, pageSize } }),
  getComingSoon: (limit = 10) => api.get('/api/Home/ComingSoon', { params: { limit } }),
  // Aggregate: everything above in one request (handy for the initial Home screen load)
  getAll: () => api.get('/api/Home/All'),
};

// ---- Legal pages ------------------------------------------------------------
export const legalApi = {
  list: () => api.get('/api/Legal'),
  get: (slug) => api.get(`/api/Legal/${slug}`),
  getForAdmin: () => api.get('/api/Legal/Admin'),
  update: (slug, payload) => api.put(`/api/Legal/Admin/${slug}`, payload),
  reset: (slug) => api.post(`/api/Legal/Admin/${slug}/Reset`),
};

// ---- Subscription --------------------------------------------------------------
export const subscriptionApi = {
  getPlans: () => api.get('/api/Subscription/Plans'),

  getStatus: () =>
    api.get('/api/Subscription/Status'),

  initiate: (email, planId) =>
    api.post('/api/Subscription/Initiate', { email, planId }),

  verify: (reference) =>
    api.post('/api/Subscription/Verify', { reference }),

  // Cancelling forfeits all paid time and can't be undone, so the server requires { confirm: true }.
  // Call cancelPreview() first and show the user its `warning` text, then call cancel(true).
  cancelPreview: () => api.get('/api/Subscription/CancelPreview'),
  cancel: (confirm = false) => api.post('/api/Subscription/Cancel', { confirm }),
};

// ---- Admin -----------------------------------------------------------------
export const adminApi = {
  // Content approval
  getPendingMovies: () => api.get('/api/Admin/PendingMovies'),
  approveMovie: (movieId, status) => api.post(`/api/Admin/ApproveMovie?movieId=${movieId}`, { status }),
  getPendingEpisodes: () => api.get('/api/Admin/PendingEpisodes'),
  approveEpisode: (episodeId, status) => api.post(`/api/Admin/ApproveEpisode?episodeId=${episodeId}`, { status }),

  // Content approved but not yet live — waiting on (or stuck on) their Bunny.net push
  getProcessingContent: () => api.get('/api/Admin/ProcessingContent'),
  retryPushEpisode: (id) => api.post(`/api/Admin/Storage/Episodes/${id}/PushToBunny`),
  retryPushTrailer: (id) => api.post(`/api/Admin/Storage/Movies/${id}/PushTrailerToBunny`),
  getStorageSummary: () => api.get('/api/Admin/Storage/Summary'),

  // Full catalog (Approved / Processing / Coming Soon / Rejected) for classification management
  getCatalog: (page = 1, pageSize = 30, search) =>
    api.get('/api/Admin/Catalog', { params: { page, pageSize, search: search || undefined } }),

  // Categories
  getCategories: () => api.get('/api/Admin/Categories'),
  createCategory: (name) => api.post('/api/Admin/Categories', { name }),
  updateCategory: (id, name) => api.put(`/api/Admin/Categories/${id}`, { name }),
  deleteCategory: (id) => api.delete(`/api/Admin/Categories/${id}`),
  uploadCategoryImage: (id, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/api/Admin/Categories/${id}/Image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Edit an already-uploaded movie/series (any approval status, incl. live) — metadata plus
  // optional thumbnail/trailer reupload
  getMovieForEdit: (movieId) => api.get(`/api/Admin/Movies/${movieId}`),
  updateMovie: (movieId, { title, description, director, cast, releaseYear, categoryId, categoryIds, thumbnailFile, posterFile, trailerFile, isKidsContent, rating, classificationAuthority, contentDescriptors }) => {
    const formData = new FormData();
    if (title != null) formData.append('Title', title);
    if (description != null) formData.append('Description', description);
    if (director != null) formData.append('Director', director);
    if (cast != null) formData.append('Cast', cast);
    if (releaseYear != null) formData.append('ReleaseYear', releaseYear);
    if (categoryId != null) formData.append('CategoryId', categoryId);
    if (categoryIds != null) categoryIds.forEach((id) => formData.append('CategoryIds', id));
    if (thumbnailFile) formData.append('Thumbnail', thumbnailFile);
    if (trailerFile) formData.append('Trailer', trailerFile);
    if (isKidsContent != null) formData.append('IsKidsContent', isKidsContent);
    if (rating != null) formData.append('Rating', rating);
    if (classificationAuthority != null) formData.append('ClassificationAuthority', classificationAuthority);
    if (contentDescriptors != null) formData.append('ContentDescriptors', contentDescriptors);
    if (posterFile) formData.append('Poster', posterFile);
    return api.put(`/api/Admin/Movies/${movieId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0, // trailer reupload can be a large file
    });
  },

  // Edit an already-uploaded episode (any approval status, incl. live) — metadata plus
  // optional video reupload
  updateEpisode: (episodeId, { title, description, episodeNumber, seasonNumber, videoFile }) => {
    const formData = new FormData();
    if (title != null) formData.append('Title', title);
    if (description != null) formData.append('Description', description);
    if (episodeNumber != null) formData.append('EpisodeNumber', episodeNumber);
    if (seasonNumber != null) formData.append('SeasonNumber', seasonNumber);
    if (videoFile) formData.append('VideoFile', videoFile);
    return api.put(`/api/Admin/Episodes/${episodeId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0, // video reupload can be a large file
    });
  },

  // Creator edit requests awaiting review (current vs. proposed values), and the ruling on one
  getPendingMovieEdits: () => api.get('/api/Admin/MovieEdits'),
  reviewMovieEdit: (editId, status, note) =>
    api.post(`/api/Admin/MovieEdits/${editId}/Review`, { status, note }),
  getPendingEpisodeEdits: () => api.get('/api/Admin/EpisodeEdits'),
  reviewEpisodeEdit: (editId, status, note) =>
    api.post(`/api/Admin/EpisodeEdits/${editId}/Review`, { status, note }),

  // Movie Home-screen classification
  setMovieContentType: (movieId, contentType) =>
    api.put(`/api/Admin/Movies/${movieId}/ContentType`, null, { params: { contentType } }),
  setMovieComingSoon: (movieId, isComingSoon, releaseDate) =>
    api.put(`/api/Admin/Movies/${movieId}/ComingSoon`, null, { params: { isComingSoon, releaseDate } }),
  // Parental controls: tag/untag a title as safe for a Kids profile (Profile.Type === 'Kids').
  setMovieKidsContent: (movieId, isKidsContent) =>
    api.put(`/api/Admin/Movies/${movieId}/KidsContent`, null, { params: { isKidsContent } }),

  // Movie cast
  addCastMember: (movieId, actorId, characterName, sortOrder = 0) =>
    api.post(`/api/Admin/Movies/${movieId}/Cast`, { actorId, characterName, sortOrder }),
  removeCastMember: (movieId, actorId) =>
    api.delete(`/api/Admin/Movies/${movieId}/Cast/${actorId}`),

  // Actors
  getActors: () => api.get('/api/Admin/Actors'),
  createActor: (name, bio, imageFile) => {
    const formData = new FormData();
    formData.append('name', name);
    if (bio) formData.append('bio', bio);
    if (imageFile) formData.append('image', imageFile);
    return api.post('/api/Admin/Actors', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  updateActor: (id, name, bio, imageFile) => {
    const formData = new FormData();
    if (name != null) formData.append('name', name);
    if (bio != null) formData.append('bio', bio);
    if (imageFile) formData.append('image', imageFile);
    return api.put(`/api/Admin/Actors/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteActor: (id) => api.delete(`/api/Admin/Actors/${id}`),

  // Subscription plans
  getSubscriptionPlans: () => api.get('/api/Admin/SubscriptionPlans'),
  createSubscriptionPlan: (payload) => api.post('/api/Admin/SubscriptionPlans', payload),
  updateSubscriptionPlan: (id, payload) => api.put(`/api/Admin/SubscriptionPlans/${id}`, payload),
  deleteSubscriptionPlan: (id) => api.delete(`/api/Admin/SubscriptionPlans/${id}`),

  // System config
  getAllConfigs: () => api.get('/api/Admin/SystemConfig'),
  updateConfig: (key, value, description) => api.put(`/api/Admin/SystemConfig/${key}`, { value, description }),
  createConfig: (key, value, description) => api.post('/api/Admin/SystemConfig', { key, value, description }),
  deleteConfig: (key) => api.delete(`/api/Admin/SystemConfig/${key}`),

  // Subscriptions
  getSubscriptions: (page = 1, pageSize = 30) =>
    api.get('/api/Admin/Subscriptions', { params: { page, pageSize } }),

  // Users
  getUsers: (page = 1, pageSize = 30) =>
    api.get('/api/Admin/Users', { params: { page, pageSize } }),
  updateUser: (userId, data) => api.put(`/api/Admin/Users/${userId}`, data),
  makeAdmin: (userId) => api.post(`/api/Admin/Users/${userId}/MakeAdmin`),
  makeContentCreator: (userId) => api.post(`/api/Admin/Users/${userId}/MakeContentCreator`),
  revokeToUser: (userId) => api.post(`/api/Admin/Users/${userId}/RevokeToUser`),
  grantUploadAccess: (userId) => api.post(`/api/Admin/Users/${userId}/GrantUploadAccess`),
  revokeUploadAccess: (userId) => api.post(`/api/Admin/Users/${userId}/RevokeUploadAccess`),
  getContentCreators: () => api.get('/api/Admin/ContentCreators'),
  getContentCreatorContent: (userId) => api.get(`/api/Admin/ContentCreators/${userId}/Content`),
};

// ---- Payout engine ----------------------------------------------------------
export const payoutApi = {
  // Admin: config
  getConfig: () => api.get('/api/Payout/Config'),
  updateConfig: (config) => api.put('/api/Payout/Config', config),

  // Admin: catalog tier
  setTier: (movieId, tier) => api.post('/api/Payout/Tier', { movieId, tier }),

  // Admin: auto-calculated gross revenue suggestion from actual subscription payments
  getRevenueSuggestion: (year, month) => api.get('/api/Payout/RevenueSuggestion', { params: { year, month } }),

  // Watch session heartbeat (any subscriber, called from the player)
  heartbeat: (episodeId, clientSessionId, secondsWatchedTotal, deviceFingerprint) =>
    api.post('/api/Payout/Heartbeat', { episodeId, clientSessionId, secondsWatchedTotal, deviceFingerprint }),

  // Admin: monthly runs
  runPayout: ({ year, month, grossRevenueNaira, deductionsNaira, finalize }) =>
    api.post('/api/Payout/Run', { year, month, grossRevenueNaira, deductionsNaira, finalize }),
  getRun: (year, month) => api.get('/api/Payout/Run', { params: { year, month } }),
  getRunById: (id) => api.get(`/api/Payout/Run/${id}`),
  listRuns: () => api.get('/api/Payout/Runs'),

  // Producer dashboard
  myPayouts: (year, month) => api.get('/api/Payout/MyPayouts', { params: { year, month } }),
};

export default api;
